import { formatISO, parseISO } from "date-fns";
import {
    ActorKind,
    AuditLogChangeType,
    ActorType,
    HistoryItem,
    ResourceType,
} from "@common/types/auditLog";
import {
    HistoryGroupBy,
    SCHEDULE_RESOURCE_TYPES,
} from "@common/constants/history-source-types";
import { APIProcessName } from "@common/constants/process-names";
import type { HistoryRow } from "../views/historyViewConfig";
import { resolveActorKind } from "./actorUtils";

function isHumanLikeActor(actorType: string): boolean {
    return (
        actorType === ActorType.USER ||
        actorType === ActorType.SUPPORT ||
        actorType === ActorType.PERSONAL_ACCESS_TOKEN ||
        actorType === ActorType.MCP_OAUTH ||
        actorType === ActorType.MCP ||
        actorType === ActorType.CLI ||
        actorType === ActorType.AGENT
    );
}

const MERGED_AGENT_KINDS: ReadonlySet<ActorKind> = new Set([ActorKind.KICK]);

const PER_ACTION_RESOURCE_TYPES: ReadonlySet<ResourceType> = new Set([
    ResourceType.chartOfAccountsTemplateApplyEvent,
    ...SCHEDULE_RESOURCE_TYPES,
]);

const PER_ACTION_SOURCE_TYPES: ReadonlySet<string> = new Set([
    APIProcessName.COUNTERPARTY_TIN_EXPORT,
]);

function isPerActionRow(row: HistoryRow): boolean {
    return (
        PER_ACTION_SOURCE_TYPES.has(row.sourceType) ||
        (row.historyItem.groupBy === HistoryGroupBy.SOURCE_COUNT_DISTINCT &&
            PER_ACTION_RESOURCE_TYPES.has(row.resourceType))
    );
}

const CHANGE_TYPE_RANK: Partial<Record<AuditLogChangeType, number>> = {
    [AuditLogChangeType.CREATE]: 0,
    [AuditLogChangeType.UPDATE]: 1,
    [AuditLogChangeType.DELETE]: 2,
};

function sortDedupedChangeTypes(
    changeTypes: AuditLogChangeType[],
): AuditLogChangeType[] {
    const seen = new Set<AuditLogChangeType>();
    for (const changeType of changeTypes) {
        seen.add(changeType);
    }
    return [...seen].sort(
        (a, b) => (CHANGE_TYPE_RANK[a] ?? 99) - (CHANGE_TYPE_RANK[b] ?? 99),
    );
}

function stableFieldKey(fields: string[]): string {
    return [...fields].sort((a, b) => a.localeCompare(b)).join("|") || "empty";
}

function stableEntityKey(entityIds: number[]): string {
    return [...entityIds].sort((a, b) => a - b).join("|");
}

function stableChangeTypesKey(changeTypes: AuditLogChangeType[]): string {
    return sortDedupedChangeTypes(changeTypes).join("|");
}

function stableInvocationKey(row: HistoryRow): string {
    const sourceIds = [...row.historyItem.sourceIds].sort((left, right) =>
        left.localeCompare(right),
    );
    if (sourceIds.length > 0) {
        return `source:${sourceIds.join("|")}`;
    }
    const presentation = row.historyItem.presentation;
    const eventRefs =
        presentation?.kind === "coaTemplateApply" ? presentation.eventRefs : [];
    const eventIds = eventRefs
        .map((eventRef) => eventRef.eventId)
        .sort((left, right) => left.localeCompare(right));
    if (eventIds.length > 0) {
        return `event:${eventIds.join("|")}`;
    }
    return `resource:${[...row.resourceIds]
        .sort((left, right) => left.localeCompare(right))
        .join("|")}`;
}

function actorKeySegment(row: HistoryRow): string {
    return String(
        row.actorId ?? row.historyItem.organizationActorId ?? "system",
    );
}

function getMergeGroupKey(row: HistoryRow): string {
    const dateKey = formatISO(row.date, { representation: "date" });
    const kind = resolveActorKind({
        actorType: row.actorType,
        sourceType: row.sourceType,
        fields: row.fields,
        changeTypes: row.changeTypes,
        system: row.system ?? null,
    });
    const usesAgentMergeSegment =
        MERGED_AGENT_KINDS.has(kind) && !isHumanLikeActor(row.actorType);
    const systemSegment = usesAgentMergeSegment ? kind : (row.system ?? "null");
    const revertedSegment = row.revertedAt ? "reverted" : "active";
    const sourceIdSegment = isPerActionRow(row)
        ? stableInvocationKey(row)
        : "mergeable";
    return [
        dateKey,
        actorKeySegment(row),
        stableChangeTypesKey(row.changeTypes),
        row.sourceType,
        row.resourceType,
        stableEntityKey(row.entityIds),
        stableFieldKey(row.fields),
        systemSegment,
        revertedSegment,
        sourceIdSegment,
        row.channel ?? "none",
    ].join("::");
}

function mergeResourceIdsPreservingOrder(rows: HistoryRow[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const row of rows) {
        for (const resourceId of row.resourceIds) {
            if (!seen.has(resourceId)) {
                seen.add(resourceId);
                result.push(resourceId);
            }
        }
    }
    return result;
}

function mergeEntityIds(rows: HistoryRow[]): number[] {
    const seen = new Set<number>();
    const result: number[] = [];
    for (const row of rows) {
        for (const entityId of row.entityIds) {
            if (!seen.has(entityId)) {
                seen.add(entityId);
                result.push(entityId);
            }
        }
    }
    return [...result].sort((a, b) => a - b);
}

function mergeSourceIds(rows: HistoryRow[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const row of rows) {
        for (const sourceId of row.historyItem.sourceIds) {
            if (!seen.has(sourceId)) {
                seen.add(sourceId);
                result.push(sourceId);
            }
        }
    }
    return result;
}

interface MergedHistoryItemParams {
    template: HistoryItem;
    mergedResourceIds: string[];
    mergedEntityIds: number[];
    mergedSourceIds: string[];
    mergedChangeTypes: AuditLogChangeType[];
    latestDate: Date;
    mergedCount: number;
    mergedSystem: string | null;
}

function buildMergedHistoryItem(params: MergedHistoryItemParams): HistoryItem {
    const {
        template,
        mergedResourceIds,
        mergedEntityIds,
        mergedSourceIds,
        mergedChangeTypes,
        latestDate,
        mergedCount,
        mergedSystem,
    } = params;
    return {
        ...template,
        date: latestDate,
        count: mergedCount,
        resourceIds: mergedResourceIds,
        entityIds: mergedEntityIds,
        sourceIds: mergedSourceIds,
        changeTypes: mergedChangeTypes,
        system: mergedSystem,
    };
}

function buildMergedHistoryRowId(row: HistoryRow): string {
    const fieldKey = stableFieldKey(row.fields).replace(/\|/g, "-");
    const resourceIds = row.resourceIds.join("-");
    const dateMs = parseISO(row.date.toString()).getTime();
    return `${dateMs}-${row.sourceType}-${actorKeySegment(row)}-${fieldKey}-${resourceIds}-${row.channel ?? "none"}`;
}

/** Coalesce history rows that differ only by `system` tier for the same agent. */
export function mergeHistoryRowsByAgent(rows: HistoryRow[]): HistoryRow[] {
    const buckets = new Map<string, HistoryRow[]>();
    const firstIndexByKey = new Map<string, number>();

    rows.forEach((row, index) => {
        const key = getMergeGroupKey(row);
        const existing = buckets.get(key);
        if (!existing) {
            buckets.set(key, [row]);
            firstIndexByKey.set(key, index);
            return;
        }
        existing.push(row);
        const prevMin = firstIndexByKey.get(key) ?? index;
        if (index < prevMin) {
            firstIndexByKey.set(key, index);
        }
    });

    const sortedKeys = [...firstIndexByKey.entries()].sort(
        (a, b) => a[1] - b[1],
    );

    return sortedKeys.flatMap(([key]) => {
        const bucket = buckets.get(key);
        if (!bucket?.length) {
            return [];
        }
        if (bucket.length === 1) {
            return [bucket[0]];
        }

        const first = bucket[0];
        const latestDate = bucket.reduce(
            (latest, row) => (row.date > latest ? row.date : latest),
            first.date,
        );
        const mergedCount = bucket.reduce((sum, row) => sum + row.count, 0);
        const mergedResourceIds = mergeResourceIdsPreservingOrder(bucket);
        const mergedEntityIds = mergeEntityIds(bucket);
        const mergedSourceIds = mergeSourceIds(bucket);
        const mergedChangeTypes = sortDedupedChangeTypes(
            bucket.flatMap((row) => row.changeTypes),
        );
        const kind = resolveActorKind({
            actorType: first.actorType,
            sourceType: first.sourceType,
            fields: first.fields,
            changeTypes: first.changeTypes,
            system: first.system ?? null,
        });
        const mergedSystem =
            MERGED_AGENT_KINDS.has(kind) && !isHumanLikeActor(first.actorType)
                ? null
                : first.system;

        const mergedHistoryItem = buildMergedHistoryItem({
            template: first.historyItem,
            mergedResourceIds,
            mergedEntityIds,
            mergedSourceIds,
            mergedChangeTypes,
            latestDate,
            mergedCount,
            mergedSystem: mergedSystem ?? null,
        });

        const merged: HistoryRow = {
            ...first,
            id: "",
            date: latestDate,
            count: mergedCount,
            resourceIds: mergedResourceIds,
            entityIds: mergedEntityIds,
            changeTypes: mergedChangeTypes,
            system: mergedSystem,
            historyItem: mergedHistoryItem,
            revertedAt: first.revertedAt,
        };

        merged.id = buildMergedHistoryRowId(merged);

        return [merged];
    });
}
